import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import browseHistoryApi, { RecentView } from '../client/browse-history-api'
import { useAuth } from './AuthContext'

// Single source of truth for browse history on the web. All three surfaces
// (header search recents, profile Recently viewed tab, the Recent drawer)
// read from here, so one fetch per session and every surface updates live
// when a view is recorded or removed. The data itself is server-side
// (Media.BrowseHistory), so it persists across devices and sessions.

export type BrowseViewDescriptor = {
  mediaType: 'Movie' | 'Show'
  tmdbId: number
  title: string
  posterUrl?: string
  year?: string
  genres?: string[]
}

type BrowseHistoryContextType = {
  recentViews: RecentView[]
  isLoaded: boolean
  recordView: (descriptor: BrowseViewDescriptor) => Promise<void>
  removeView: (mediaType: string, tmdbId: number) => Promise<void>
  clearViews: () => Promise<void>
}

const BrowseHistoryContext = createContext<BrowseHistoryContextType | undefined>(undefined)

const FETCH_COUNT = 40

const viewKey = (mediaType: string, tmdbId: number) =>
  `${mediaType.toLowerCase()}-${tmdbId}`

const BrowseHistoryProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, getAccessToken } = useAuth()
  const [recentViews, setRecentViews] = useState<RecentView[]>([])
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) {
      setRecentViews([])
      setIsLoaded(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const token = await getAccessToken()
        if (!token || cancelled) return
        const res = await browseHistoryApi.getRecent(FETCH_COUNT, token)
        if (!cancelled) setRecentViews(res.data)
      } catch {
        if (!cancelled) setRecentViews([])
      } finally {
        if (!cancelled) setIsLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, getAccessToken])

  // Fire-and-forget capture with an optimistic prepend so the drawer and
  // search recents update the moment you open a detail page. Throttled to
  // one server event per title per browser session.
  const recordView = useCallback(
    async (descriptor: BrowseViewDescriptor) => {
      if (!isAuthenticated) return

      const sessionKey = `browse_view_${descriptor.mediaType}_${descriptor.tmdbId}`
      try {
        if (sessionStorage.getItem(sessionKey)) return
        sessionStorage.setItem(sessionKey, '1')
      } catch {
        /* storage blocked: fall through and record anyway */
      }

      const releaseYear = descriptor.year ? parseInt(descriptor.year, 10) : NaN
      const optimistic: RecentView = {
        tmdbId: descriptor.tmdbId,
        mediaType: descriptor.mediaType,
        title: descriptor.title,
        posterPath: descriptor.posterUrl,
        releaseYear: Number.isNaN(releaseYear) ? undefined : releaseYear,
        genres: descriptor.genres ?? [],
        lastViewedOnUtc: new Date().toISOString(),
      }
      setRecentViews((previous) => [
        optimistic,
        ...previous.filter(
          (view) => viewKey(view.mediaType, view.tmdbId) !== viewKey(descriptor.mediaType, descriptor.tmdbId)
        ),
      ])

      try {
        const token = await getAccessToken()
        if (!token) return
        await browseHistoryApi.recordView(
          {
            mediaType: descriptor.mediaType,
            tmdbMediaId: descriptor.tmdbId,
            title: descriptor.title,
            posterPath: descriptor.posterUrl,
            releaseYear: optimistic.releaseYear ?? undefined,
            genresJson: descriptor.genres ? JSON.stringify(descriptor.genres) : undefined,
          },
          token
        )
      } catch {
        /* capture must never surface an error; the optimistic row stays */
      }
    },
    [isAuthenticated, getAccessToken]
  )

  const removeView = useCallback(
    async (mediaType: string, tmdbId: number) => {
      setRecentViews((previous) =>
        previous.filter((view) => viewKey(view.mediaType, view.tmdbId) !== viewKey(mediaType, tmdbId))
      )
      try {
        const token = await getAccessToken()
        if (!token) return
        await browseHistoryApi.removeItem(mediaType, tmdbId, token)
      } catch {
        /* optimistic removal stands; worst case it reappears next session */
      }
    },
    [getAccessToken]
  )

  const clearViews = useCallback(async () => {
    setRecentViews([])
    const token = await getAccessToken()
    if (!token) return
    await browseHistoryApi.clear(token)
  }, [getAccessToken])

  return (
    <BrowseHistoryContext.Provider
      value={{ recentViews, isLoaded, recordView, removeView, clearViews }}
    >
      {children}
    </BrowseHistoryContext.Provider>
  )
}

export const useBrowseHistory = () => {
  const ctx = useContext(BrowseHistoryContext)
  if (!ctx) throw new Error('useBrowseHistory must be used within BrowseHistoryProvider')
  return ctx
}

export default BrowseHistoryProvider
